<template>
    <CompassModal
        v-model:visible="displayModal"
        class="address-form-modal"
        :title="modalTitle"
        :size="CompassEnums.ModalSize.SMALL"
        :primaryAction="primaryAction"
        :cancelButtonProps="cancelButtonProps"
        @closed="handleClose"
    >
        <div class="address-form-modal__body">
            <div class="address-form-modal__input-container">
                <template v-if="isLoading">
                    <CompassSkeleton size="x-large" />
                    <CompassSkeleton variant="block" />
                    <CompassSkeleton v-if="address" size="x-large" />
                    <CompassSkeleton v-if="address" size="x-large" />
                    <CompassSkeleton v-if="address" size="x-large" />
                </template>
                <template v-else>
                    <CompassFormItem
                        :label="$t.__('common.labels.address_name')"
                        class="address-form-modal__address-name"
                        :info="$t.__('common.labels.info_address_name')"
                        :error="errors.addressName"
                    >
                        <CompassInput
                            v-model="localAddress.name"
                            :error="!!errors.addressName"
                            @input="errors.addressName = ''"
                        />
                    </CompassFormItem>
                    <AutocompleteAddress
                        v-if="!manualMode"
                        :errorMsg="errors.address_line_1"
                        @update:address="localAddress = { ...localAddress, ...$event }"
                        @input="errors.address_line_1 = ''"
                    />
                    <template v-else>
                        <CompassFormItem
                            :label="$t.__('common.labels.address')"
                            :error="addressErrors"
                            class="address-form-modal__create-manual-street"
                        >
                            <CompassInput
                                v-model="localAddress.address_line_1"
                                :error="!!errors.address_line_1"
                                @input="errors.address_line_1 = ''"
                            />
                            <CompassInput
                                v-model="localAddress.address_line_2"
                                :error="!!errors.address_line_2"
                                @input="errors.address_line_2 = ''"
                            />
                            <CompassInput
                                v-model="localAddress.address_line_3"
                                :error="!!errors.address_line_3"
                                @input="errors.address_line_3 = ''"
                            />
                            <CompassInput
                                v-model="localAddress.address_line_4"
                                :error="!!errors.address_line_4"
                                @input="errors.address_line_4 = ''"
                            />
                        </CompassFormItem>
                        <CompassFormItem
                            :label="$t.__('common.labels.country')"
                            class="address-form-modal__create-manual-country"
                            :error="errors.country_code"
                        >
                            <CompassSelect
                                v-model="localAddress.country_code"
                                menuClass="select-country-code"
                                :error="!!errors.country_code"
                                @change="errors.country_code = ''"
                            >
                                <CompassDropdownOption
                                    v-for="country in countries"
                                    :key="country.code"
                                    :label="country.name"
                                    :value="country.code"
                                />
                            </CompassSelect>
                        </CompassFormItem>
                        <div class="address-form-modal__zip-and-city">
                            <CompassFormItem
                                :label="$t.__('common.labels.postal_code')"
                                class="address-form-modal__create-manual-postal"
                                :error="errors.postal_code"
                            >
                                <CompassInput
                                    v-model="localAddress.postal_code"
                                    :error="!!errors.postal_code"
                                    @input="errors.postal_code = ''"
                                />
                            </CompassFormItem>
                            <CompassFormItem
                                :label="$t.__('common.labels.city')"
                                class="address-form-modal__create-manual-city"
                                :error="errors.city"
                            >
                                <CompassInput
                                    v-model="localAddress.city"
                                    :error="!!errors.city"
                                    @input="errors.city = ''"
                                />
                            </CompassFormItem>
                        </div>
                    </template>
                </template>
            </div>
            <CompassPlainButton
                v-if="!manualMode && !isLoading"
                class="address-form-modal__button-switch-mode"
                :size="CompassEnums.PlainButtonSize.SMALL"
                :label="
                    $t.__('document.DocFormSettingsDialog.FormDeliveryAddress.btn_to_manual_create')
                "
                @click="manualMode = true"
            />
        </div>
    </CompassModal>
</template>

<script>
import AutocompleteAddress from 'vcomponents/common/address/AutocompleteAddress.vue';
import { createNamespacedHelpers } from 'vuex';
const { mapState: corpState } = createNamespacedHelpers('corp');

export default {
    components: {
        AutocompleteAddress,
    },
    props: {
        address: {
            type: [Object, null],
            default: null,
        },
        linkedid: {
            type: Number,
            default: 0,
        },
        apiEndpoint: {
            type: String,
            default: '',
        },
        isAccountAddress: {
            type: Boolean,
            default: false,
        },
        callback: {
            type: Function,
            default: () => {},
        },
    },
    data() {
        return {
            countries: [],
            displayModal: false,
            isLoading: true,
            isSubmitting: false,
            localAddress: {
                name: '',
                address_line_1: '',
                address_line_2: '',
                address_line_3: '',
                address_line_4: '',
                postal_code: '',
                city: '',
                country_code: '',
            },
            errors: {
                addressName: '',
                address_line_1: '',
                address_line_2: '',
                address_line_3: '',
                address_line_4: '',
                postal_code: '',
                city: '',
                country_code: '',
            },
            manualMode: false,
            maxLengthInput: {
                name: 200,
                address_line_1: 250,
                address_line_2: 250,
                address_line_3: 250,
                address_line_4: 250,
                postal_code: 30,
                city: 250,
            },
        };
    },
    computed: {
        ...corpState(['defaultAddressCountry']),
        primaryAction() {
            return {
                label: this.$t.__('common.actions.save'),
                action: this.save,
                loading: this.isSubmitting,
            };
        },
        cancelButtonProps() {
            return {
                label: this.$t.__('common.actions.cancel'),
                action: this.handleClose,
                disabled: this.isSubmitting,
            };
        },
        modalTitle() {
            return this.address
                ? this.$t.__('common.labels.edit_address')
                : this.$t.__('common.actions.add_address');
        },
        addressErrors() {
            return (
                this.errors.address_line_1 ||
                this.errors.address_line_2 ||
                this.errors.address_line_3 ||
                this.errors.address_line_4
            );
        },
    },
    watch: {
        manualMode(newManualMode) {
            if (newManualMode) {
                this.displayManualMode();
            }
        },
    },
    async created() {
        this.manualMode = !!this.address;

        const promises = [];
        if (this.countries.length === 0) {
            promises.push(
                ApiClient.get('/countries').then((response) => {
                    this.countries = response.data?.data ?? [];
                }),
            );
        }
        if (this.address) {
            const { name, address_line_1, postal_code, city, country_code } = this.address;
            this.localAddress = {
                name,
                address_line_1,
                address_line_2: this.address.address_line_2 || '',
                address_line_3: this.address.address_line_3 || '',
                address_line_4: this.address.address_line_4 || '',
                postal_code,
                city,
                country_code,
            };
        }

        if (promises.length > 0) {
            this.isLoading = true;
            await Promise.all(promises);
        }

        this.isLoading = false;
    },
    mounted() {
        this.displayModal = true;
    },
    methods: {
        validateAddress() {
            const requiredErrorMsg = this.$t.__('common.labels.this_field_is_required');
            const minLength = 2;
            const minimumErrorMsg = this.$t.n__(
                'common.labels.field_minimum_characters',
                null,
                minLength,
                minLength,
            );
            const maximumErrorMsg = (maxLength) => {
                return this.$t.n__(
                    'common.labels.field_maximum_characters',
                    null,
                    maxLength,
                    maxLength,
                );
            };

            this.errors.address_line_1 = !this.localAddress.address_line_1 ? requiredErrorMsg : '';

            if (!this.localAddress.name) {
                this.errors.addressName = requiredErrorMsg;
            } else if (this.localAddress.name.length < minLength) {
                this.errors.addressName = minimumErrorMsg;
            } else if (this.localAddress.name.length > this.maxLengthInput.name) {
                this.errors.addressName = maximumErrorMsg(this.maxLengthInput.name);
            } else {
                this.errors.addressName = '';
            }

            if (this.manualMode) {
                for (const key of Object.keys(this.errors)) {
                    if (['postal_code', 'city', 'country_code'].includes(key)) {
                        if (this.localAddress[key] === '') {
                            this.errors[key] = requiredErrorMsg;
                        } else if (this.localAddress[key].length < minLength) {
                            this.errors[key] = minimumErrorMsg;
                        } else {
                            this.errors[key] = '';
                        }
                    }
                }

                for (const [key, maxLength] of Object.entries(this.maxLengthInput)) {
                    if (this.localAddress[key] && this.localAddress[key].length > maxLength) {
                        this.errors[key] = maximumErrorMsg(maxLength);
                    }
                }
            }

            return !Object.values(this.errors).some((error) => !!error);
        },
        async save() {
            this.isSubmitting = true;
            if (!this.validateAddress()) {
                this.isSubmitting = false;
                return;
            }

            try {
                if (this.address) {
                    const endpoint = this.isAccountAddress
                        ? `/addresses/${this.address.id}`
                        : `${this.apiEndpoint}/${this.linkedid}/addresses/${this.address.id}`;

                    const { data } = await ApiClient.put(endpoint, {}, this.localAddress);
                    EventBus.emit('edit-address', data);
                } else {
                    const endpoint = this.isAccountAddress
                        ? '/addresses'
                        : `${this.apiEndpoint}/${this.linkedid}/addresses`;

                    const { data } = await ApiClient.post(endpoint, {}, this.localAddress);
                    EventBus.emit('add-address', data);
                }
                this.$pushToast({
                    type: 'success',
                    icon: true,
                    text: this.address
                        ? this.$t.__('common.labels.edit_address_success')
                        : this.$t.__('common.labels.create_address_success'),
                });
                this.handleClose();
            } catch (e) {
                this.$pushToast({
                    type: 'error',
                    icon: true,
                    text: this.address
                        ? this.$t.__('common.labels.edit_address_error')
                        : this.$t.__('common.labels.create_address_error'),
                });
                throw e;
            } finally {
                this.isSubmitting = false;
            }
        },
        async displayManualMode() {
            if (!this.localAddress.country_code) {
                this.localAddress.country_code = this.defaultAddressCountry ?? 'FR';
            }
        },
        handleClose() {
            this.displayModal = false;
            this.$nextTick(() => {
                this.callback();
                this.$emit('close');
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.address-form-modal {
    &__body {
        display: flex;
        flex-direction: column;
    }

    &__input-container {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-4);
    }

    &__button-switch-mode {
        align-self: start;
        margin-top: var(--spacing-0);
    }

    &__zip-and-city {
        display: flex;
        gap: var(--spacing-3);
    }

    &__create-manual-postal {
        width: 80px;
    }

    &__create-manual-city {
        flex: 1;
    }
}
</style>
<style lang="scss">
#compass-dropdown-container {
    .select-country-code {
        z-index: 1001;
    }
}
</style>
